import { type RouteObject } from 'react-router'

import { lazyComponent } from 'routes/lazy-route'
import {
  DASHBOARDS_2_DETAIL_TRANSACTION_PATH,
  DASHBOARDS_2_DETAIL_URL,
  DASHBOARDS_2_EDIT_TRANSACTION_PATH,
  DASHBOARDS_2_EDIT_URL,
  DASHBOARDS_2_TEMPLATE_SETUP_TRANSACTION_PATH,
  DASHBOARDS_2_TRANSACTION_PATH,
  DASHBOARDS_2_URL,
  LEGACY_SHARE_CUSTOM_DASHBOARD,
  SHARE_DASHBOARD_V2,
} from 'src/features/dashboards-v2/paths'

/** Warms the edit page's lazy chunk ahead of navigation — call this
 *  fire-and-forget right when a "create dashboard" action starts, in
 *  parallel with the network request that creates the dashboard, so the
 *  chunk fetch (which currently happens only after navigate() runs) isn't a
 *  second, sequential wait on top of the create request. Kept beside the
 *  route's own `lazy` import below so the two specifiers can't drift apart. */
export const prefetchDashboardsV2EditPage = (): void => {
  void import('src/features/dashboards-v2/features/edit')
}

export const sharedDashboardsV2Routes: RouteObject[] = [
  {
    path: SHARE_DASHBOARD_V2,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2/features/shared'),
      'DashboardSharePage',
    ),
  },
  {
    // /project/:id/shared-dashboard/:token — legacy v1 share slug, kept
    // alive for old emailed links; use-legacy-redirect resolves
    // ?dashboard_id= and navigates to the v2 slug.
    path: LEGACY_SHARE_CUSTOM_DASHBOARD,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2/features/shared'),
      'DashboardSharePage',
    ),
  },
]

export const dashboardsV2Routes: RouteObject[] = [
  {
    path: DASHBOARDS_2_URL,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2/features/list-v2'),
      'Dashboards2ListPage',
    ),
    handle: {
      featureKey: DASHBOARDS_2_URL,
      sentryTransactionPath: DASHBOARDS_2_TRANSACTION_PATH,
    },
  },
  {
    path: `${DASHBOARDS_2_URL}/templates/:templateId/setup`,
    lazy: lazyComponent(
      () =>
        import('src/features/dashboards-v2/features/templates/features/setup'),
      'DashboardTemplateSetupPage',
    ),
    handle: {
      // Piggy-back on the list feature key so users with dashboards access
      // reach setup without a separate access rule.
      featureKey: DASHBOARDS_2_URL,
      sentryTransactionPath: DASHBOARDS_2_TEMPLATE_SETUP_TRANSACTION_PATH,
    },
  },
  {
    path: `${DASHBOARDS_2_URL}/:customDashboardId`,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2/features/view-v2'),
      'Dashboards2ViewPage',
    ),
    handle: {
      featureKey: DASHBOARDS_2_DETAIL_URL,
      sentryTransactionPath: DASHBOARDS_2_DETAIL_TRANSACTION_PATH,
    },
  },
  {
    path: `${DASHBOARDS_2_URL}/:customDashboardId/edit`,
    // The embeddable-card frame wrapper lives in edit/Root.tsx now (was
    // inlined here as `V2EditWrapper`) so this route can dynamic-import one
    // self-contained module instead of statically pulling in
    // styled-components at the route-table level.
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2/features/edit'),
      'DashboardEditPage',
    ),
    handle: {
      featureKey: DASHBOARDS_2_EDIT_URL,
      sentryTransactionPath: DASHBOARDS_2_EDIT_TRANSACTION_PATH,
    },
  },
]
